import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Form, Alert, Spinner, Badge, Accordion } from 'react-bootstrap';
import {
    getUserPermissions,
    assignUserPermissions,
    revokeUserPermissions,
    syncUserPermissions,
    getAvailablePermissions,
    clearUserPermissionsState
} from '../../actions/userPermissions';

const UserPermissionManager = ({ show, handleClose, user }) => {
    const dispatch = useDispatch();
    const {
        userPermissions,
        availablePermissions,
        loading,
        assignLoading,
        revokeLoading,
        syncLoading,
        availableLoading,
        error,
        success
    } = useSelector(state => state.userPermissions);

    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [mode, setMode] = useState('assign'); // assign, revoke, sync

    useEffect(() => {
        if (show && user) {
            dispatch(getUserPermissions(user.id));
            dispatch(getAvailablePermissions());
        }
    }, [show, user, dispatch]);

    useEffect(() => {
        if (!show) {
            dispatch(clearUserPermissionsState());
            setSelectedPermissions([]);
            setMode('assign');
        }
    }, [show, dispatch]);

    const handlePermissionToggle = (permissionId) => {
        setSelectedPermissions(prev => {
            if (prev.includes(permissionId)) {
                return prev.filter(id => id !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (selectedPermissions.length === 0) {
            return;
        }

        switch (mode) {
            case 'assign':
                await dispatch(assignUserPermissions(user.id, selectedPermissions));
                break;
            case 'revoke':
                await dispatch(revokeUserPermissions(user.id, selectedPermissions));
                break;
            case 'sync':
                await dispatch(syncUserPermissions(user.id, selectedPermissions));
                break;
            default:
                break;
        }

        setSelectedPermissions([]);
    };

    const isPermissionSelected = (permissionId) => {
        return selectedPermissions.includes(permissionId);
    };

    const hasPermission = (permissionId) => {
        const permission = availablePermissions.find(group => 
            group.permissions.find(p => p.id === permissionId)
        )?.permissions.find(p => p.id === permissionId);
        
        if (!permission) {
            return false;
        }
        
        // Use effective permissions instead of all permissions
        return userPermissions.effective_permissions?.includes(permission.name);
    };

    const isRolePermission = (permissionId) => {
        return userPermissions.role_permissions?.some(p => p.id === permissionId);
    };

    const isDirectPermission = (permissionId) => {
        return userPermissions.direct_permissions?.some(p => p.id === permissionId);
    };

    const isDeniedPermission = (permissionId) => {
        return userPermissions.denied_permissions?.some(p => p.id === permissionId);
    };

    const getPermissionBadge = (permissionId) => {
        const badges = [];
        
        if (isRolePermission(permissionId)) {
            badges.push(<Badge key="role" bg="primary" className="ms-1">Rol</Badge>);
        }
        if (isDirectPermission(permissionId)) {
            badges.push(<Badge key="direct" bg="success" className="ms-1">To'g'ridan-to'g'ri</Badge>);
        }
        if (isDeniedPermission(permissionId)) {
            badges.push(<Badge key="denied" bg="danger" className="ms-1">Rad etilgan</Badge>);
        }
        
        return badges;
    };

    const canRevokePermission = (permissionId) => {
        // Allow revoking any permission that the user currently has
        // This includes both direct permissions and role-based permissions
        return hasPermission(permissionId);
    };

    const renderPermissionGroups = () => {
        if (!availablePermissions || !Array.isArray(availablePermissions)) {
            return <div>No permissions available</div>;
        }
        
        return availablePermissions.map((group, index) => (
            <Accordion.Item eventKey={index.toString()} key={index}>
                <Accordion.Header>
                    {group.group} ({group.permissions.length})
                </Accordion.Header>
                <Accordion.Body>
                    {group.permissions && group.permissions.length > 0 ? group.permissions.map(permission => (
                        <div key={permission.id} className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id={`permission-${permission.id}`}
                                checked={isPermissionSelected(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                                disabled={
                                    (mode === 'revoke' && !canRevokePermission(permission.id)) ||
                                    (mode === 'assign' && hasPermission(permission.id))
                                }
                            />
                            <label className="form-check-label d-flex align-items-center" htmlFor={`permission-${permission.id}`}>
                                <span className={hasPermission(permission.id) ? 'text-success' : ''}>
                                    {permission.display_name || permission.name}
                                </span>
                                {getPermissionBadge(permission.id)}
                            </label>
                        </div>
                    )) : <div>No permissions in this group</div>}
                </Accordion.Body>
            </Accordion.Item>
        ));
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Ruxsatlarni boshqarish - {user?.name}
                    {user?.role_name && (
                        <Badge bg="secondary" className="ms-2">{user.role_name}</Badge>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {loading || availableLoading ? (
                    <div className="text-center">
                        <Spinner animation="border" />
                        <p>Ruxsatlar yuklanmoqda...</p>
                    </div>
                ) : (
                    <>
                        {/* Permission Summary */}
                        <div className="mb-4">
                            <h6>Joriy ruxsatlar xulasasi</h6>
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body text-center">
                                            <h5 className="card-title text-primary">
                                                {userPermissions.role_permissions?.length || 0}
                                            </h5>
                                            <p className="card-text">Rol ruxsatlari</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body text-center">
                                            <h5 className="card-title text-success">
                                                {userPermissions.direct_permissions?.length || 0}
                                            </h5>
                                            <p className="card-text">To'g'ridan-to'g'ri ruxsatlar</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body text-center">
                                            <h5 className="card-title text-danger">
                                                {userPermissions.denied_permissions?.length || 0}
                                            </h5>
                                            <p className="card-text">Rad etilgan ruxsatlar</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body text-center">
                                            <h5 className="card-title text-info">
                                                {userPermissions.effective_permissions?.length || 0}
                                            </h5>
                                            <p className="card-text">Faol ruxsatlar</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mode Selection */}
                        <div className="mb-3">
                            <h6>Harakat rejimi</h6>
                            <div className="btn-group" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="mode"
                                    id="assign-mode"
                                    value="assign"
                                    checked={mode === 'assign'}
                                    onChange={(e) => setMode(e.target.value)}
                                />
                                <label className="btn btn-outline-success" htmlFor="assign-mode">
                                    Ruxsatlar berish
                                </label>

                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="mode"
                                    id="revoke-mode"
                                    value="revoke"
                                    checked={mode === 'revoke'}
                                    onChange={(e) => setMode(e.target.value)}
                                />
                                <label className="btn btn-outline-danger" htmlFor="revoke-mode">
                                    Ruxsatlarni bekor qilish
                                </label>

                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="mode"
                                    id="sync-mode"
                                    value="sync"
                                    checked={mode === 'sync'}
                                    onChange={(e) => setMode(e.target.value)}
                                />
                                <label className="btn btn-outline-primary" htmlFor="sync-mode">
                                    Ruxsatlarni sinxronlash
                                </label>
                            </div>
                            <small className="form-text text-muted d-block mt-1">
                                {mode === 'assign' && 'Foydalanuvchiga yangi ruxsatlar qo\'shish'}
                                {mode === 'revoke' && 'Foydalanuvchidan ruxsatlarni olib tashlash (rol va to\'g\'ridan-to\'g\'ri ruxsatlar)'}
                                {mode === 'sync' && 'Barcha to\'g\'ridan-to\'g\'ri ruxsatlarni tanlangan bilan almashtirish'}
                            </small>
                        </div>

                        {/* Permission Selection */}
                        <Form onSubmit={handleSubmit}>
                            <h6>Mavjud ruxsatlar</h6>
                            <Accordion>
                                {renderPermissionGroups()}
                            </Accordion>

                            <div className="mt-3">
                                <p>
                                    <strong>Tanlangan: {selectedPermissions.length}</strong>
                                </p>
                                <div className="d-flex gap-2">
                                    <Button
                                        type="submit"
                                        variant={mode === 'assign' ? 'success' : mode === 'revoke' ? 'danger' : 'primary'}
                                        disabled={selectedPermissions.length === 0 || assignLoading || revokeLoading || syncLoading}
                                    >
                                        {(assignLoading || revokeLoading || syncLoading) && (
                                            <Spinner animation="border" size="sm" className="me-2" />
                                        )}
                                        {mode === 'assign' && 'Tanlanganlarni berish'}
                                        {mode === 'revoke' && 'Tanlanganlarni bekor qilish'}
                                        {mode === 'sync' && 'Tanlanganlarni sinxronlash'}
                                    </Button>
                                    <Button variant="secondary" onClick={handleClose}>
                                        Bekor qilish
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default UserPermissionManager; 